/**
 * RestoID — link an email address as a 2FA factor.
 * ---------------------------------------------------------------------------
 * RestoID frame around the PRISTINE upstream `VerificationCodeMfaBinding`
 * logic, instantiated the way `src/pages/MfaBinding/EmailMfaBinding` does it
 * (identifierType = Email, `invalid_email` as the guard error):
 *   - flow state ......... `useMfaFlowState` + the same invalid-session guard
 *   - send the code ...... `sendVerificationCode(InteractionEvent.Register, …)`
 *   - hand-off ........... `setVerificationId` / `setIdentifierInputValue` then
 *                          `/continue/verification-code` with the SAME state
 *                          shape (`{ flow: MfaBinding, mfaFlowState }`)
 *   - error branch ....... `useErrorHandler` with the `guard.invalid_input`
 *                          handler; everything else still falls through to the
 *                          upstream toast
 *   - skip ............... `useSkipMfa` / `useSkipOptionalMfa`, chosen by
 *                          `skippable` + `suggestion` exactly as upstream
 *   - switch factor ...... shared `MfaSwitchLink` (same >1-factor condition)
 * Swapped in for `./pages/MfaBinding/EmailMfaBinding` via `src/App.tsx`.
 *
 * Deliberate differences from upstream:
 * 1. SecondaryPageLayout (NavBar + its own duplicated title/description) is
 *    replaced by FirstScreenLayout + a single centred `ScreenHead`. The NavBar's
 *    back action becomes ScreenHead's back pill, and its "skip" becomes the
 *    bottom `GhostAction` — the same place `custom/pages/MfaBinding` puts it.
 *    Upstream's description (`mfa.link_email_2fa_description`, "Link your email
 *    address for 2-step verification") only restates the title ("Link your email
 *    address"), so it is dropped rather than repeated under it as a `sub` — the
 *    same call MfaTotpBinding and MfaBackupCodeBinding make.
 * 2. `IdentifierProfileForm` + `SmartInputField` become a RestoID `Field`. The
 *    form is still react-hook-form with upstream's `reValidateMode: 'onBlur'`,
 *    upstream's validate rules and upstream's clear-on-invalid effect; only the
 *    control is ours. Field has one error slot, so the field-level message wins
 *    over the API error, which upstream rendered just below in its own
 *    `ErrorMessage` — the identical position.
 * 3. Upstream passed `experienceSettings?.usernamePolicy` to
 *    `validateIdentifierField`; it is only ever read for a Username identifier,
 *    and this screen is Email-only, so the PageContext read is dropped.
 * 4. Upstream fed the raw `invalid_email` KEY into `<ErrorMessage>` as children,
 *    which renders untranslated — it shows the literal string "invalid_email".
 *    We translate it (`t('error.invalid_email')`), which is plainly what was
 *    meant; the branch itself is untouched.
 */
import {
  InteractionEvent,
  SignInIdentifier,
  type VerificationCodeIdentifier,
} from '@logto/schemas';
import { type FormEvent, useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { sendVerificationCode } from '@/apis/experience';
import useErrorHandler from '@/hooks/use-error-handler';
import useMfaFlowState from '@/hooks/use-mfa-factors-state';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useSkipMfa from '@/hooks/use-skip-mfa';
import useSkipOptionalMfa from '@/hooks/use-skip-optional-mfa';
import { UserMfaFlow } from '@/types';
import { getGeneralIdentifierErrorMessage, validateIdentifierField } from '@/utils/form';
import { codeVerificationTypeMap } from '@/utils/sign-in-experience';

import { MfaSwitchLink } from '../components/MfaFactorRows';
import { Button, Field, GhostAction, ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

type FormState = { email: string };

const MfaEmailBinding = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const mfaFlowState = useMfaFlowState();
  const { setVerificationId, setIdentifierInputValue } = useContext(UserInteractionContext);
  const [errorMessage, setErrorMessage] = useState<string>();

  const skipMfa = useSkipMfa();
  const skipOptionalMfa = useSkipOptionalMfa();
  const handleError = useErrorHandler();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormState>({
    reValidateMode: 'onBlur',
    defaultValues: { email: '' },
  });

  const clearErrorMessage = useCallback(() => {
    setErrorMessage('');
  }, []);

  // Upstream IdentifierProfileForm: the lookup error is dropped as soon as the
  // form stops being valid, so a stale "invalid email" never outlives the input.
  useEffect(() => {
    if (!isValid) {
      clearErrorMessage();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: FormEvent<HTMLFormElement>) => {
      clearErrorMessage();

      await handleSubmit(async ({ email }) => {
        const identifier: VerificationCodeIdentifier = {
          type: SignInIdentifier.Email,
          value: email,
        };

        try {
          const result = await sendVerificationCode(InteractionEvent.Register, identifier);

          setVerificationId(codeVerificationTypeMap[SignInIdentifier.Email], result.verificationId);
          setIdentifierInputValue(identifier);

          navigate('/continue/verification-code', {
            state: {
              flow: UserMfaFlow.MfaBinding,
              mfaFlowState,
            },
          });
        } catch (error) {
          await handleError(error, {
            'guard.invalid_input': () => {
              setErrorMessage(t('error.invalid_email'));
            },
          });
        }
      })(event);
    },
    [
      clearErrorMessage,
      handleError,
      handleSubmit,
      mfaFlowState,
      navigate,
      setIdentifierInputValue,
      setVerificationId,
      t,
    ]
  );

  if (!mfaFlowState) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const { skippable, suggestion } = mfaFlowState;
  const onSkip = skippable ? (suggestion ? skipOptionalMfa : skipMfa) : undefined;
  // One error slot: the field's own validation message wins over the API error.
  const shownError = errors.email?.message ?? errorMessage;

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'mfa.link_email_verification_code_description' }}>
      <Stack gap={20}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('mfa.link_email_verification_code_description')}
        />
        <form onSubmit={onSubmitHandler}>
          <Stack gap={18}>
            <Controller
              control={control}
              name="email"
              rules={{
                validate: (value) => {
                  if (!value) {
                    return getGeneralIdentifierErrorMessage([SignInIdentifier.Email], 'required');
                  }

                  const invalid = validateIdentifierField(SignInIdentifier.Email, value);

                  if (!invalid) {
                    return true;
                  }

                  return typeof invalid === 'string'
                    ? t(`error.${invalid}`)
                    : t(`error.${invalid.code}`, invalid.data ?? {});
                },
              }}
              render={({ field: { value, onChange, onBlur, ref } }) => (
                <Field
                  autoFocus
                  // The html contract upstream's SmartInputField gave a
                  // single-type email field, plus `input.email` as the label.
                  label={t('input.email')}
                  inputType="email"
                  inputMode="email"
                  autoComplete="off"
                  value={value}
                  inputRef={ref}
                  danger={Boolean(shownError)}
                  errorMessage={shownError}
                  // Upstream's SmartInputField trimmed on every change
                  // (`use-smart-input-field` -> `setInputValue(value.trim())`), and
                  // `emailRegEx` is /^\S+@\S+\.\S+$/ — so without this a pasted
                  // " a@corp.com " would be rejected as invalid where upstream
                  // accepted it. Trim here, not at submit, to match upstream exactly.
                  onChange={(next) => {
                    onChange(next.trim());
                  }}
                  onBlur={onBlur}
                />
              )}
            />
            <Button title={t('action.continue')} htmlType="submit" isLoading={isSubmitting} />
          </Stack>
        </form>
        <MfaSwitchLink flow={UserMfaFlow.MfaBinding} flowState={mfaFlowState} />
        {onSkip ? <GhostAction onClick={() => onSkip()}>{t('action.nav_skip')}</GhostAction> : null}
      </Stack>
    </FirstScreenLayout>
  );
};

export default MfaEmailBinding;
